import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import type {
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    AutocompleteCloseReason,
    AutocompletePropsSizeOverrides,
    AutocompleteRenderValue,
    AutocompleteRenderValueGetItemProps,
    AutocompleteRenderOptionState,
    AutocompleteValue,
    FilterOptionsState,
    FormControlOwnProps,
    UseAutocompleteProps,
} from "@mui/material";
import type { ChipProps } from "@mui/material/Chip";
import { FormattedMessage, useIntl } from "react-intl";
import { HTMLAttributes, ReactNode, SyntheticEvent, useEffect, useMemo, useRef, useState, type JSX } from "react";
import type { OverridableStringUnion } from "@mui/types";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import type { SxProps, Theme } from "@mui/system";
import findIndex from "lodash/findIndex";
import uniqBy from "lodash/uniqBy";
import { CreateOption, ResponseResult } from "./GenericAutocompleteTypes";
import { TooltipWrapper } from "../TooltipWrapper/TooltipWrapper";
import useAbortApi from "../../Hooks/useAbortApi";
import { BO_CORE_DEFAULT_NUMBER_VALUE, fieldVariant } from "../../utils/AppUtils";

interface IProps<
    T extends CreateOption,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
> extends UseAutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
    id: string;
    listboxId: string;
    value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;
    options: readonly T[];
    optionPropKey?: string;
    label: ReactNode;
    placeholder: string;
    error: boolean;
    errorMessage: string | ReactNode;
    onChange: (
        event: SyntheticEvent,
        value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<T>,
    ) => void;
    getOptionLabel: (option: any) => string;
    getResponseResult: (limit: number, page: number, searchText: string, signal: AbortSignal) => Promise<ResponseResult>;
    listSize?: number;
    listHeight?: number;
    excludeId?: number;
    customFilterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
    interval?: number;
    multiple?: Multiple;
    clearOnBlur?: boolean;
    required?: boolean;
    onBlur?: () => void;
    getOptionDisabled?: (option: T) => boolean;
    chipProps?: ChipProps<any>;
    renderTags?: (value: T[], getItemProps: AutocompleteRenderValueGetItemProps<Multiple>) => ReactNode;
    sx?: SxProps<Theme>;
    sxAutocomplete?: object;
    freeSolo?: FreeSolo;
    selectOnFocus?: boolean;
    renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: T, state: AutocompleteRenderOptionState) => ReactNode;
    size?: OverridableStringUnion<"small" | "medium", AutocompletePropsSizeOverrides>;
    inputMargin?: FormControlOwnProps["margin"];
    disabled?: boolean;
    disableClearable?: DisableClearable;
    disablePortal?: boolean;
    clearIcon?: ReactNode;
    clearText?: string;
    closeText?: string;
    forcePopupIcon?: true | false | "auto";
    noOptionsText?: ReactNode;
    openText?: string;
    popupIcon?: ReactNode;
    getGenericOptions?: (options: T[]) => void;
    forceUpdate?: boolean;
    resetForceUpdate?: () => void;
    forceUpdateFilter?: string;
    clearInputValueOnSelect?: boolean;
    fetchWithOffset?: boolean; // need to use offset with graphql queries
    tooltipText?: string;
    customFormHelperText?: ReactNode;
    autoFocusTextField?: boolean;
    additionalOption?: T;
    startAdornment?: React.ReactNode;
    additionalLastOption?: T;
    additionalFirstOption?: T | null;
    disableInput?: boolean;
    setIsOptionListOpen?: (val: boolean) => void;
    isOptionListOpen?: boolean;
    onScrollListItems?: () => void;
    readOnly?: boolean;
    openOnFocus?: boolean;
}

const startPage = 0;
const startLastIndex = 0;
// distance from the bottom of the listbox at which the next page is requested
const scrollBottomThreshold = 10;
// safety net for the automatic paging that fills a non-scrollable listbox: no realistic option list
// needs this many consecutive pages to overflow, so stop rather than walk a very large data set
const maxConsecutiveFillRequests = 20;
// let timer: any;

type OptionalOption<T> = T | null | undefined;

const pickRecordsToShow = <T extends CreateOption>(
    records: T[],
    options: {
        freeSolo: boolean | undefined;
        additionalFirstOption: OptionalOption<T>;
        additionalOption: OptionalOption<T>;
        additionalLastOption: OptionalOption<T>;
        pageNumber: number;
        searchText: string;
        total: number;
        size: number;
        limit: number;
        updateOptionRecordsInFreeSolo: (records: T[], searchText: string) => T[];
    },
): T[] => {
    const {
        freeSolo,
        additionalFirstOption,
        additionalOption,
        additionalLastOption,
        pageNumber,
        searchText,
        total,
        size,
        limit,
        updateOptionRecordsInFreeSolo,
    } = options;
    if (freeSolo && total <= limit && searchText.length > 0) {
        return updateOptionRecordsInFreeSolo(records, searchText);
    }
    if (additionalFirstOption && pageNumber === 0) {
        return [additionalFirstOption, ...records];
    }
    if (additionalOption && pageNumber === 0 && searchText.trim().length === 0) {
        return [additionalOption, ...records];
    }
    if (additionalLastOption && size === total) {
        return [...records, additionalLastOption];
    }
    return [...records];
};

const GenericAutocomplete = <
    T extends CreateOption,
    Multiple extends boolean | undefined = boolean,
    DisableClearable extends boolean | undefined = boolean,
    FreeSolo extends boolean | undefined = boolean,
>({
    id,
    listboxId,
    value,
    options,
    optionPropKey = "id",
    label,
    placeholder,
    error,
    errorMessage,
    onChange,
    getOptionLabel,
    getResponseResult,
    listSize,
    listHeight,
    excludeId,
    customFilterOptions,
    interval,
    multiple,
    clearOnBlur,
    required,
    onBlur,
    getOptionDisabled,
    chipProps,
    renderTags,
    sx,
    sxAutocomplete,
    freeSolo,
    selectOnFocus,
    renderOption,
    size,
    inputMargin,
    disabled,
    disableClearable,
    disablePortal = true,
    clearIcon,
    clearText,
    closeText,
    forcePopupIcon,
    noOptionsText,
    openText,
    popupIcon,
    getGenericOptions,
    forceUpdate,
    resetForceUpdate,
    forceUpdateFilter,
    clearInputValueOnSelect,
    fetchWithOffset,
    tooltipText,
    customFormHelperText,
    autoFocusTextField,
    additionalOption,
    startAdornment,
    additionalLastOption,
    additionalFirstOption,
    disableInput,
    setIsOptionListOpen,
    isOptionListOpen,
    onScrollListItems,
    readOnly,
    openOnFocus,
    ...restPassedProps
}: IProps<T, Multiple, DisableClearable, FreeSolo>): JSX.Element => {
    type OptionsType = typeof options;

    const { formatMessage } = useIntl();
    const { signal } = useAbortApi();

    const timer = useRef<NodeJS.Timeout>(undefined);
    // set once a paged request comes back empty, so the listbox is not refilled indefinitely
    const hasExhaustedOptions = useRef<boolean>(false);
    const consecutiveFillRequests = useRef<number>(0);
    // `listboxId` is not unique across the app - several call sites reuse the same value - so the
    // listbox has to be reached through this instance's own ref rather than looked up by id.
    // MUI forks refs passed via `slotProps.listbox`, and sets it back to null when the popup closes.
    const listboxNodeRef = useRef<HTMLElement | null>(null);

    const [shouldFilterOptions, setShouldFilterOptions] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>("");
    const [genericOptions, setGenericOptions] = useState<T[]>([]);
    const [page, setPage] = useState<number>(startPage);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<string>("");
    const [lastFilter, setLastFilter] = useState<string>("");
    const [lastIndex, setLastIndex] = useState<number>(startLastIndex);
    const [isFreeSolo, setIsFreeSolo] = useState<boolean | undefined>(freeSolo);
    // bumped whenever the option list is opened, so the fill check below runs again even if
    // reopening did not schedule a new request
    const [fillCheckTrigger, setFillCheckTrigger] = useState<number>(0);

    const pageSize: number = useMemo(() => listSize || 10, [listSize]);
    const typingInterval: number = useMemo(() => interval || 600, [interval]);
    const listboxHeight: number = useMemo(() => listHeight || 200, [listHeight]);

    const setNewIdFromValue = (valueToMakeId: OptionsType): number => {
        let newId: number;
        const optionIds: number[] = [];
        valueToMakeId.forEach((element: T) => {
            if (element.createdValue) {
                optionIds.push(element[optionPropKey]);
            }
        });
        if (optionIds.length > 0) {
            const lastId = Math.min(...optionIds);
            newId = lastId - 1;
        } else {
            newId = BO_CORE_DEFAULT_NUMBER_VALUE;
        }
        return newId;
    };

    const checkIfOptionRecordsContainsSearchText = (optionsArray: T[], searchText: string): boolean => {
        const foundOption = optionsArray
            .filter((option: T) => {
                const optionKeys = Object.keys(option).filter((key) => key !== optionPropKey);
                let result: T | undefined;
                optionKeys.forEach((key: string) => {
                    if (option[key] === searchText) {
                        result = option[key];
                    }
                });
                return result;
            })
            .filter((el) => el !== undefined);
        return foundOption.length > 0;
    };

    const updateOptionRecordsInFreeSolo = (records, searchText: string): T[] => {
        if (multiple) {
            const optionExist: boolean = checkIfOptionRecordsContainsSearchText(records, searchText);
            if (!optionExist) {
                const currentValue = value as OptionsType;
                if (currentValue && currentValue.length > 0) {
                    const existingValueOption = currentValue.find((element) => element.createdValue === searchText);
                    if (existingValueOption) {
                        records.push(existingValueOption);
                    } else {
                        const newId = setNewIdFromValue(currentValue);
                        records.push({ id: newId, createdValue: searchText });
                    }
                } else {
                    records.push({ id: BO_CORE_DEFAULT_NUMBER_VALUE, createdValue: searchText });
                }
            }
        } else {
            const optionExist: boolean = checkIfOptionRecordsContainsSearchText(records, searchText);
            if (!optionExist) {
                records.push({ id: BO_CORE_DEFAULT_NUMBER_VALUE, createdValue: searchText });
            }
        }

        return records;
    };

    const updatePagingAndSetIsLoadingFalse = (recordsCount: number, recordsNum: number): void => {
        if (recordsCount > 0) {
            const numberOfPages = Math.ceil(recordsCount / pageSize);
            setTotalPages(numberOfPages);
        }
        setPage((prevPage: number) => prevPage + 1);
        setLastIndex((oldIndex) => oldIndex + recordsNum);
        setIsLoading(false);
    };

    const resetPaging = (): void => {
        setPage(startPage);
        setLastIndex(startLastIndex);
        setTotalPages(1);
        hasExhaustedOptions.current = false;
        consecutiveFillRequests.current = 0;
    };

    const resetPagingAndOptionsAndSetIsLoadingTrue = (): void => {
        setIsLoading(true);
        resetPaging();
        setGenericOptions([]);
    };

    const resetPagingAndOptionsInFreeSolo = (searchText: string): void => {
        resetPaging();
        const newOptions: any[] = [];

        if (multiple) {
            const currentValue = value as OptionsType;
            if (currentValue && currentValue.length > 0) {
                const existingValueOption = currentValue.find((element) => element.createdValue === searchText);
                if (existingValueOption) {
                    newOptions.push(existingValueOption);
                } else {
                    const newId = setNewIdFromValue(currentValue);
                    newOptions.push({ id: newId, createdValue: searchText });
                }
            } else {
                newOptions.push({ id: BO_CORE_DEFAULT_NUMBER_VALUE, createdValue: searchText });
            }
        } else {
            newOptions.push({ id: BO_CORE_DEFAULT_NUMBER_VALUE, createdValue: searchText });
        }

        setGenericOptions(newOptions);
        setIsLoading(false);
    };

    const resetFilter = (): void => {
        setFilter("");
    };

    // POSSIBLE A11Y BUG: This function can receive an out-of-bounds index (e.g. -1 or
    // genericOptions.length), which sets aria-activedescendant to a non-existent option ID.
    // MUI then creates a phantom empty <li role="option"> with no accessible name,
    // violating WCAG aria-toggle-field-name. See a11y test todos for details.
    const highlightActiveChild = (eventTarget: HTMLInputElement, index: number, listboxNode: Element | null): void => {
        eventTarget.setAttribute("aria-activedescendant", `${id}-option-${index}`);
        if (listboxNode) {
            const activeChild = listboxNode.querySelector(`[data-option-index="${index}"]`) as HTMLElement;
            activeChild?.classList?.add("Mui-focused");
            activeChild?.classList?.add("Mui-focusVisible");
        }
    };

    const highlightActiveChildOnScroll = (event: SyntheticEvent<Element, MouseEvent | KeyboardEvent>, option: T): void => {
        const eventCurTarget = event.currentTarget as unknown as HTMLElement;
        const eventTarget = event.target as HTMLInputElement;
        if (eventCurTarget?.parentElement && eventTarget) {
            const listboxNode = eventCurTarget.parentElement.querySelector(`[role="listbox"]`);
            const index = genericOptions.indexOf(option);
            highlightActiveChild(eventTarget, index, listboxNode);
        }
    };

    const highlightActiveChildOnKeyDownAndScrollIfOptionPresent = (event: SyntheticEvent<Element, MouseEvent | KeyboardEvent>, option: T): void => {
        const currentIndex = genericOptions.indexOf(option);
        const eventCurTarget = event.currentTarget as unknown as HTMLElement;
        const eventTarget = event.target as HTMLInputElement;
        if (eventCurTarget?.parentElement && eventTarget) {
            const listboxNode = eventCurTarget.parentElement.querySelector(`[role="listbox"]`);
            const lastChildListboxNode = listboxNode!.querySelector(`[data-option-index="${currentIndex}"]`) as HTMLElement;
            let index: number;

            // @ts-ignore
            if (event.key === "ArrowDown") {
                index = currentIndex + 1;
                // @ts-ignore
            } else if (event.key === "ArrowUp") {
                index = currentIndex - 1;
            } else {
                index = currentIndex;
            }

            // POSSIBLE A11Y BUG: index can exceed genericOptions bounds (ArrowDown past last
            // or ArrowUp past first), producing a phantom option. See a11y test todos.

            // highlight change in UI
            if (lastChildListboxNode) {
                lastChildListboxNode.classList.remove("Mui-focused");
                lastChildListboxNode.classList.remove("Mui-focusVisible");
            }
            eventTarget.setAttribute("aria-activedescendant", `${id}-option-${index}`);
            const activeChild = listboxNode!.querySelector(`[data-option-index="${index}"]`) as HTMLElement;
            activeChild.classList.add("Mui-focused");
            activeChild.classList.add("Mui-focusVisible");

            const activeChildHeight: number = activeChild.offsetTop + activeChild.clientHeight;
            // @ts-ignore
            if (event.key === "ArrowDown") {
                if (activeChildHeight > listboxNode!.clientHeight && activeChildHeight - listboxNode!.clientHeight > listboxNode!.scrollTop) {
                    listboxNode!.scrollTop = activeChildHeight - listboxNode!.clientHeight;
                }
            }
            // @ts-ignore
            if (event.key === "ArrowUp") {
                if (activeChildHeight > listboxNode!.clientHeight && activeChildHeight - listboxNode!.clientHeight > listboxNode!.scrollTop) {
                    listboxNode!.scrollTop = activeChildHeight - listboxNode!.clientHeight;
                } else if (activeChildHeight > listboxNode!.clientHeight && activeChildHeight === listboxNode!.scrollTop) {
                    listboxNode!.scrollTop = activeChild.offsetTop;
                } else if (
                    activeChildHeight > listboxNode!.clientHeight &&
                    listboxNode!.scrollTop - activeChildHeight > 0 &&
                    listboxNode!.scrollTop - activeChildHeight < activeChild.clientHeight
                ) {
                    listboxNode!.scrollTop = activeChild.offsetTop;
                }
                if (activeChildHeight < listboxNode!.clientHeight && activeChildHeight <= listboxNode!.scrollTop) {
                    listboxNode!.scrollTop = activeChild.offsetTop;
                }
            }
        }
    };

    const highlightActiveChildOnKeyDownIfOptionNotPresent = (event: SyntheticEvent<Element, MouseEvent | KeyboardEvent>): void => {
        const eventCurTarget = event.currentTarget as unknown as HTMLElement;
        const eventTarget = event.target as HTMLInputElement;
        if (eventCurTarget?.parentElement && eventTarget) {
            const listboxNode = eventCurTarget.parentElement.querySelector(`[role="listbox"]`);
            let index: number;

            // @ts-ignore
            if (event.key === "ArrowDown") {
                index = 0;
                // @ts-ignore
            } else if (event.key === "ArrowUp") {
                // POSSIBLE A11Y BUG: -1 creates a phantom option with no accessible name.
                // See a11y test todos for details.
                index = -1;
            } else {
                index = 0;
            }

            highlightActiveChild(eventTarget, index, listboxNode);
        }
    };

    const onHighlightChange = (event: SyntheticEvent<Element, MouseEvent | KeyboardEvent>, option: T | null, reason: string): void => {
        if (reason === "auto") {
            return;
        }
        if (event && reason === "keyboard") {
            event.preventDefault();
            event.stopPropagation();
        }

        if (event.type === "mouseover") {
            return;
        }

        if (event?.type === "keydown") {
            if (option && reason === "keyboard" && genericOptions.length > 0) {
                highlightActiveChildOnKeyDownAndScrollIfOptionPresent(event, option);
            } else if (!option && reason === "keyboard" && genericOptions.length > 0) {
                highlightActiveChildOnKeyDownIfOptionNotPresent(event);
            }
        }

        if (event?.type === "scroll" && option) {
            highlightActiveChildOnScroll(event, option);
        }
    };

    /**
     * A rejected request must still clear `isLoading`. Left true it silently disables both paging
     * paths - each guards on the loading flag - with no spinner to show why, because the loading
     * state only renders while there are no options yet. `stopPaging` additionally prevents the
     * automatic fill below from immediately retrying a failing endpoint in a tight loop.
     */
    const handlePagingRequestFailure = (stopPaging = false): void => {
        if (signal.aborted) {
            return;
        }
        if (stopPaging) {
            hasExhaustedOptions.current = true;
        }
        setIsLoading(false);
        if (resetForceUpdate) {
            resetForceUpdate();
        }
        if (shouldFilterOptions) {
            setShouldFilterOptions(false);
        }
    };

    const getOptions = (limit: number, pageNumber: number, searchText: string): void => {
        getResponseResult(limit, pageNumber, searchText, signal)
            .then((res) => {
                if (signal.aborted) {
                    return;
                }
                const responseResult = res;
                const records: T[] = responseResult.elements;
                if (records.length > 0) {
                    const testIdNum = excludeId || undefined;
                    const newRecords = pickRecordsToShow<T>(records, {
                        freeSolo,
                        additionalFirstOption,
                        additionalOption,
                        additionalLastOption,
                        pageNumber,
                        searchText,
                        total: responseResult.total,
                        size: responseResult.size,
                        limit,
                        updateOptionRecordsInFreeSolo,
                    });

                    setGenericOptions(() => newRecords.filter((record: T) => record[optionPropKey] !== testIdNum));

                    setLastFilter(filter);
                    updatePagingAndSetIsLoadingFalse(responseResult.total, responseResult.size);
                } else if ((additionalLastOption || additionalFirstOption) && records.length === 0) {
                    if (additionalFirstOption && additionalLastOption) {
                        setGenericOptions([additionalFirstOption, additionalLastOption]);
                        return;
                    }
                    if (additionalFirstOption) {
                        setGenericOptions([additionalFirstOption]);
                    }
                    if (additionalLastOption) {
                        setGenericOptions([additionalLastOption]);
                    }
                } else if (freeSolo) {
                    resetPagingAndOptionsInFreeSolo(searchText);
                } else {
                    resetPaging();
                    setGenericOptions([]);
                    setIsLoading(false);
                }

                if (resetForceUpdate) {
                    resetForceUpdate();
                }
                if (shouldFilterOptions) {
                    setShouldFilterOptions(false);
                }
            })
            .catch(handlePagingRequestFailure);
    };

    // `lastChildListboxNode`, `listboxNode` and `event` are only needed to restore the scroll
    // position and the highlighted option when paging was triggered by a scroll. They are omitted
    // when the next page is requested to fill a listbox that is not scrollable yet.
    const getOptionsOnScroll = (lastChildListboxNode?: HTMLElement, listboxNode?: (EventTarget & Element) | null, event?: any): void => {
        const shallowEvent = event;
        const shallowListboxNode = listboxNode;
        let pageNumber: number = page;
        if (fetchWithOffset) {
            pageNumber = lastIndex;
        }

        getResponseResult(pageSize, pageNumber, filter, signal)
            .then((res) => {
                if (signal.aborted) {
                    return;
                }
                let filtered: T[] = [...genericOptions];
                const responseResult = res;
                const records = responseResult.elements;
                if (records.length > 0) {
                    const testIdNum = excludeId || undefined;
                    filtered = filtered.concat(records.filter((record: T) => record[optionPropKey] !== testIdNum));
                    let newFiltered: T[];

                    if (freeSolo && page + 1 === totalPages && filter.length > 0) {
                        newFiltered = updateOptionRecordsInFreeSolo(filtered, filter);
                    } else if (additionalLastOption && page + 1 === totalPages) {
                        newFiltered = [...filtered, additionalLastOption];
                    } else {
                        newFiltered = [...filtered];
                    }

                    setGenericOptions(() => {
                        return uniqBy(newFiltered, optionPropKey);
                    });
                    setLastFilter(filter);
                    updatePagingAndSetIsLoadingFalse(responseResult.total, responseResult.size);
                } else {
                    // The page came back empty even though the paging state expected more records.
                    // Stop paging, otherwise a listbox that never becomes scrollable keeps requesting it.
                    hasExhaustedOptions.current = true;
                    setIsLoading(false);
                }

                // The listbox can be gone by the time the page request resolves: typing resets the
                // options (MUI swaps the listbox for the loading text), or the list was closed or the
                // component unmounted. There is then no scroll position or highlight to restore, and
                // `closest()` on the detached scroll target returns null. Bail out instead of
                // throwing and flagging the options as exhausted.
                if (lastChildListboxNode && shallowListboxNode?.isConnected) {
                    shallowListboxNode.scrollTop = lastChildListboxNode.offsetTop + lastChildListboxNode.clientHeight - shallowListboxNode.clientHeight;
                    const index = lastChildListboxNode.dataset.optionIndex;
                    if (index) {
                        const option = genericOptions[index];
                        if (!shallowEvent.currentTarget) {
                            // React clears `currentTarget` once the scroll event has been dispatched
                            const popperNode = (shallowEvent.target as Element).closest(".MuiAutocomplete-popper");
                            shallowEvent.currentTarget = popperNode?.previousElementSibling ?? null;
                        }
                        if (shallowEvent.currentTarget) {
                            onHighlightChange(shallowEvent, option, "keyboard");
                        }
                    }
                }
            })
            .catch(() => handlePagingRequestFailure(true));
    };

    const filterOptions = () => {
        let pageNumber: number = startPage;
        if (fetchWithOffset) {
            pageNumber = startLastIndex;
        }
        getOptions(pageSize, pageNumber, filter);
    };

    const checkInputValueContainsFilter = (): boolean => {
        return getOptionLabel(value).toLowerCase().includes(filter.toLowerCase());
    };

    const scheduleFilterOptions = () => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            filterOptions();
        }, typingInterval);
    };

    const onOpen = (event: SyntheticEvent): void => {
        const eventTarget = event.target as HTMLInputElement;
        // only when the value is empty and filter has been changed - multiple mode
        if ((!eventTarget.value || eventTarget.value === "") && isLoading) {
            scheduleFilterOptions();
        }
        // only when the value is empty and genericOptions is empty on initial loading
        else if ((!eventTarget.value || eventTarget.value === "") && genericOptions.length === 0) {
            setIsLoading(true);
            scheduleFilterOptions();
        } else if (filter.length > 0 && isLoading) {
            // single mode
            scheduleFilterOptions();
        } else if (filter.length > 0 && !multiple) {
            // check if value is not null and the filter has been changed
            if (lastFilter !== filter) {
                resetPagingAndOptionsAndSetIsLoadingTrue();
                scheduleFilterOptions();
            }
        } else if (forceUpdate) {
            scheduleFilterOptions();
        }
        // reopening the list can leave a short, non-scrollable set of options in place without
        // scheduling a request, so re-run the fill check
        setFillCheckTrigger((prev) => prev + 1);
    };

    const onKeyUp = (event): void => {
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
            clearTimeout(timer.current);
            timer.current = setTimeout(() => {
                filterOptions();
            }, typingInterval);
        }
    };

    const onKeyDownPreventMuiEvent = (event) => {
        const shallowEvent = event;

        shallowEvent.defaultMuiPrevented = true;
        shallowEvent.preventDefault();
    };

    const onKeyDownArrowDown = (event) => {
        const shallowEvent = event;
        shallowEvent.defaultMuiPrevented = true;
        if (genericOptions.length > 0) {
            const listboxParent = shallowEvent.currentTarget.parentElement;
            if (listboxParent) {
                const listboxNode = listboxParent.querySelector('[role="listbox"]');
                if (listboxNode) {
                    const activeChild: HTMLLIElement = listboxNode.querySelector(".Mui-focused");
                    let index: number;
                    if (activeChild) {
                        index = Array.prototype.slice.call(listboxNode.children).indexOf(activeChild);
                    } else {
                        index = -1;
                    }
                    if (!shallowEvent.target.getAttribute("aria-activedescendant")) {
                        shallowEvent.target.setAttribute("aria-activedescendant", `${id}-option-${index}`);
                    }
                    if (index < genericOptions.length - 1) {
                        const option = genericOptions[index];
                        onHighlightChange(shallowEvent as SyntheticEvent<Element, KeyboardEvent>, option, "keyboard");
                    }
                }
            }
        } else {
            shallowEvent.preventDefault();
        }
    };

    const onKeyDownArrowUp = (event) => {
        const shallowEvent = event;
        shallowEvent.defaultMuiPrevented = true;
        if (genericOptions.length > 0) {
            const listboxParent = shallowEvent.currentTarget.parentElement;
            if (listboxParent) {
                const listboxNode = listboxParent.querySelector('[role="listbox"]');
                if (listboxNode) {
                    const activeChild: HTMLLIElement = listboxNode.querySelector(".Mui-focused");
                    let index: number;
                    if (activeChild) {
                        index = Array.prototype.slice.call(listboxNode.children).indexOf(activeChild);
                    } else {
                        return;
                    }
                    if (!shallowEvent.target.getAttribute("aria-activedescendant")) {
                        shallowEvent.target.setAttribute("aria-activedescendant", `${id}-option-${index}`);
                    }
                    if (index >= 1) {
                        const option = genericOptions[index];
                        onHighlightChange(shallowEvent, option, "keyboard");
                    }
                }
            }
        } else {
            shallowEvent.preventDefault();
        }
    };

    const onClear = () => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            let pageNumber: number = startPage;
            if (fetchWithOffset) {
                pageNumber = startLastIndex;
            }
            getOptions(pageSize, pageNumber, "");
        }, typingInterval);
    };

    const onInputChange = (event: SyntheticEvent, newInputValue: string, reason: string): void => {
        if (clearInputValueOnSelect && reason === "reset") {
            setInputValue("");
        } else {
            setInputValue(newInputValue);
        }
        setLastFilter(filter);
        if (reason === "input" || reason === "clear") {
            setFilter(newInputValue);
        }
        if (reason === "clear") {
            onClear();
        }
    };

    const onClose = (event: SyntheticEvent, reason: AutocompleteCloseReason): void => {
        switch (reason) {
            case "blur":
                if (multiple) {
                    resetFilter();
                } else if (!value) {
                    resetFilter();
                } else if (!checkInputValueContainsFilter()) {
                    resetFilter();
                    onClear();
                }
                break;
            case "selectOption":
                if (multiple || clearInputValueOnSelect) {
                    resetFilter();
                }
                break;
            case "escape":
                if (multiple || !value) {
                    onInputChange(event, "", "reset");
                    resetFilter();
                } else if (!checkInputValueContainsFilter()) {
                    onInputChange(event, "", "reset");
                    resetFilter();
                    onClear();
                }
                break;
            default:
                break;
        }
    };

    const onKeyDownEnter = (event) => {
        const shallowEvent = event;
        shallowEvent.defaultMuiPrevented = true;

        if (genericOptions.length === 0) {
            shallowEvent.preventDefault();
            return;
        }

        const listboxParent = shallowEvent.currentTarget.parentElement;
        if (!listboxParent) return;

        const listboxNode = listboxParent.querySelector('[role="listbox"]');
        if (!listboxNode) return;

        const activeChild: HTMLLIElement | null = listboxNode.querySelector(".Mui-focused");
        if (!activeChild) return;

        const index = Array.prototype.slice.call(listboxNode.children).indexOf(activeChild);
        if (index === -1) return;

        const option = genericOptions[index];
        const disabledOption = getOptionDisabled ? getOptionDisabled(option) : false;
        if (disabledOption) return;

        const newInputValue = getOptionLabel(option);
        onInputChange(shallowEvent, newInputValue, "reset");

        if (multiple) {
            const newValue = Array.isArray(value) ? value.slice() : [];
            // @ts-ignore
            const itemIndex = findIndex(newValue, (valueItem) => option[optionPropKey] === valueItem[optionPropKey]);
            if (itemIndex === -1) {
                newValue.push(option);
            }
            // @ts-ignore
            onChange(shallowEvent, newValue, "selectOption");
            onClose(shallowEvent, "selectOption");
        } else {
            // @ts-ignore
            onChange(shallowEvent, option, "selectOption");
            onClose(shallowEvent, "selectOption");
            setShouldFilterOptions(true);
        }
    };

    const onKeyDown = (event): void => {
        clearTimeout(timer.current);
        if (event.key === "Home") {
            onKeyDownPreventMuiEvent(event);
        }
        if (event.key === "End") {
            onKeyDownPreventMuiEvent(event);
        }
        if (event.key === "PageUp") {
            onKeyDownPreventMuiEvent(event);
        }
        if (event.key === "PageDown") {
            onKeyDownPreventMuiEvent(event);
        }
        if (event.key === "ArrowDown") {
            onKeyDownArrowDown(event);
        }
        if (event.key === "ArrowUp") {
            onKeyDownArrowUp(event);
        }
        if (event.key === "Enter") {
            onKeyDownEnter(event);
        }
    };

    const onKeyEnterInputPreventSelectOption = (event) => {
        if (event.key === "Enter") {
            if (!event.currentTarget.getAttribute("aria-activedescendant")) {
                event.stopPropagation();
            }
        }
    };

    const infiniteScrollOptions = (event: SyntheticEvent): void => {
        const listboxNode = event.currentTarget;
        if (listboxNode.scrollHeight - (listboxNode.scrollTop + listboxNode.clientHeight) <= scrollBottomThreshold) {
            let lastChildListboxNode: HTMLElement | undefined;
            if (listboxNode.children.length > 0) {
                lastChildListboxNode = listboxNode.children[listboxNode.children.length - 1] as HTMLElement;
            }
            if (page + 1 <= totalPages && !isLoading) {
                setIsLoading(true);
                getOptionsOnScroll(lastChildListboxNode, listboxNode, event);
            }
        }
    };

    // Paging is driven by the listbox scroll event, but a listbox that does not overflow never
    // fires one. `filterSelectedOptions` removes the selected options from the rendered list (MUI
    // strips them before `filterOptions` runs) while the fetched `genericOptions` still drive
    // `page`/`totalPages`, so selecting enough options leaves too few rendered to overflow and the
    // next page is never requested. Request pages until the listbox overflows again or the options
    // run out. Each fetch updates `genericOptions`/`page`, which re-runs this effect with fresh
    // state, so it iterates one page at a time instead of looping over stale values.
    useEffect(() => {
        if (isLoading || hasExhaustedOptions.current || genericOptions.length === 0 || page + 1 > totalPages) {
            return;
        }
        if (consecutiveFillRequests.current >= maxConsecutiveFillRequests) {
            return;
        }
        const listboxNode = listboxNodeRef.current;
        // no listbox mounted means the option list is closed - nothing to fill
        if (!listboxNode) {
            return;
        }
        // a listbox that has not been laid out yet reports no height, so whether it overflows
        // cannot be determined - skip instead of assuming there is room for more options
        if (listboxNode.clientHeight === 0) {
            return;
        }
        if (listboxNode.scrollHeight - listboxNode.clientHeight > scrollBottomThreshold) {
            // the listbox overflows again, so scrolling takes over the paging from here
            consecutiveFillRequests.current = 0;
            return;
        }
        consecutiveFillRequests.current += 1;
        setIsLoading(true);
        getOptionsOnScroll();
    }, [genericOptions, value, isLoading, page, totalPages, fillCheckTrigger]);

    useEffect(() => {
        let pageNumber: number = page;
        if (fetchWithOffset) {
            pageNumber = lastIndex;
        }
        getOptions(pageSize, pageNumber, filter);
    }, [signal]);

    useEffect(() => {
        resetPagingAndOptionsAndSetIsLoadingTrue();
    }, [filter]);

    useEffect(() => {
        if (shouldFilterOptions && isLoading) {
            clearTimeout(timer.current);
            timer.current = setTimeout(() => {
                filterOptions();
            }, typingInterval);
        }
    }, [filter, shouldFilterOptions, isLoading]);

    useEffect(() => {
        if (getGenericOptions) {
            getGenericOptions(genericOptions);
        }
    }, [genericOptions]);

    useEffect(() => {
        if (forceUpdate) {
            if (forceUpdateFilter !== undefined && forceUpdateFilter !== null) {
                setFilter(forceUpdateFilter);
            }
            resetPagingAndOptionsAndSetIsLoadingTrue();
        }
    }, [forceUpdate]);

    useEffect(() => {
        // It will make the autocomplete freeSolo without adding anything extra
        // i.e this will not affect the logics that  use freeSolo prop and manipulate something in the auto complete.
        if (freeSolo) {
            return undefined;
        }

        if (genericOptions.length === 0 && value && !multiple) {
            setIsFreeSolo(true);
            return undefined;
        }

        if (genericOptions.length > 0 && value && !multiple) {
            setIsFreeSolo(!genericOptions.some((option) => option[optionPropKey] === value[optionPropKey]));
            return undefined;
        }

        return undefined;
    }, [genericOptions, freeSolo]);

    useEffect(() => {
        return () => clearTimeout(timer.current);
    }, []);

    return (
        <TooltipWrapper title={tooltipText}>
            <Autocomplete
                {...restPassedProps}
                id={id}
                fullWidth
                multiple={multiple}
                // needed to fix loading true and options, but options not rendered
                loading={isLoading && genericOptions.length === 0}
                readOnly={readOnly || false}
                openOnFocus={openOnFocus || false}
                loadingText={<FormattedMessage id="common.placeholder.loading" defaultMessage="Loading..." />}
                value={value}
                options={genericOptions}
                isOptionEqualToValue={(option, currValue) => option[optionPropKey] === currValue[optionPropKey]}
                freeSolo={(freeSolo || isFreeSolo) as FreeSolo}
                onOpen={(e: SyntheticEvent) => onOpen(e)}
                filterOptions={customFilterOptions || ((optionsInstances: T[]) => optionsInstances)}
                clearOnBlur={clearOnBlur || true}
                selectOnFocus={selectOnFocus}
                handleHomeEndKeys={false}
                onHighlightChange={(event: SyntheticEvent, option: T | null, reason: string) => {
                    onHighlightChange(event as SyntheticEvent<Element, KeyboardEvent>, option, reason);
                }}
                onChange={(event, newValue, reason) => {
                    if (reason === "removeOption") {
                        resetFilter();
                        resetPaging();
                        onClear();
                    }
                    if (reason === "blur") {
                        setFilter(inputValue);
                    }
                    if (reason === "clear") {
                        resetPaging();
                    }
                    return onChange(event, newValue, reason);
                }}
                onBlur={
                    onBlur ||
                    (() => {
                        // This is intentional
                    })
                }
                onKeyUp={(event) => onKeyUp(event)}
                onKeyDown={(event) => onKeyDown(event)}
                inputValue={inputValue}
                onInputChange={(event, newInputValue, reason) => {
                    if (disableInput && event) {
                        event.preventDefault();
                    } else {
                        if (event && freeSolo && !multiple && value?.[optionPropKey] === BO_CORE_DEFAULT_NUMBER_VALUE) {
                            onChange(event, null as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>, "clear");
                        }
                        onInputChange(event, newInputValue, reason);
                    }
                }}
                getOptionLabel={getOptionLabel}
                getOptionKey={(option) => option[optionPropKey]}
                getOptionDisabled={getOptionDisabled || (() => false)}
                renderValue={
                    renderTags as
                        | ((value: AutocompleteRenderValue<T, Multiple, FreeSolo>, getItemProps: AutocompleteRenderValueGetItemProps<Multiple>) => ReactNode)
                        | undefined
                }
                renderInput={(params) => (
                    <FormControl fullWidth sx={sx || { marginTop: 0 }} error={error}>
                        <TextField
                            {...params}
                            margin={inputMargin}
                            label={label}
                            placeholder={placeholder}
                            variant={fieldVariant}
                            sx={sx || { marginTop: 0 }}
                            error={error}
                            required={required || false}
                            autoFocus={autoFocusTextField || false}
                            onClick={() => setIsOptionListOpen?.(true)}
                            onFocus={() => setIsOptionListOpen?.(true)}
                            onBlur={() => setIsOptionListOpen?.(false)}
                            slotProps={{
                                ...params.slotProps,
                                input: startAdornment ? { ...params.slotProps.input, startAdornment } : { ...params.slotProps.input },
                                htmlInput: freeSolo
                                    ? { ...params.slotProps.htmlInput, onKeyDown: (event) => onKeyEnterInputPreventSelectOption(event) }
                                    : { ...params.slotProps.htmlInput },
                            }}
                        />
                        {customFormHelperText === undefined ? <FormHelperText>{error ? errorMessage : " "}</FormHelperText> : customFormHelperText}
                    </FormControl>
                )}
                open={isOptionListOpen}
                renderOption={renderOption}
                size={size || "medium"}
                disabled={disabled || false}
                disableClearable={disableClearable}
                disablePortal={disablePortal}
                clearIcon={clearIcon || <ClearIcon fontSize="small" />}
                clearText={clearText || formatMessage({ id: "common.button-label.clear", defaultMessage: "Clear" })}
                closeText={closeText || formatMessage({ id: "common.label.close", defaultMessage: "Close" })}
                forcePopupIcon={forcePopupIcon || "auto"}
                noOptionsText={noOptionsText || formatMessage({ id: "common.field-label.no-options", defaultMessage: "No Options" })}
                openText={openText || formatMessage({ id: "common.label.open-autocomplete", defaultMessage: "Open" })}
                popupIcon={popupIcon || <ArrowDropDownIcon />}
                onClose={(event: SyntheticEvent, reason: AutocompleteCloseReason) => onClose(event, reason)}
                sx={sxAutocomplete}
                slotProps={{
                    listbox: {
                        id: listboxId,
                        ref: listboxNodeRef,
                        style: { maxHeight: listboxHeight, overflow: "auto" },
                        role: "listbox",
                        onScroll: (event: SyntheticEvent) => {
                            if (onScrollListItems) {
                                onScrollListItems();
                            }
                            infiniteScrollOptions(event);
                        },
                    },

                    chip: chipProps || {},
                }}
            />
        </TooltipWrapper>
    );
};

export default GenericAutocomplete;
